import { useState } from 'react';

import { useBulkAction } from '@/hooks/useBulkAction/useBulkAction';
import type { UseBulkSelectionReturn } from '@/hooks/useBulkSelection/useBulkSelection';

/**
 * Payload for the bulk confirmation dialog.
 * Callers open the dialog by calling openConfirmation with this payload.
 */
export interface BulkConfirmPayload {
  /** Backend action identifier (e.g. 'status_change') */
  action: string;
  /** Human-readable label shown in the confirmation dialog title */
  actionName: string;
  /** Additional parameters forwarded to the bulk endpoint (e.g. { status: 'F' }) */
  params: Record<string, unknown>;
}

/**
 * Parameters for useBulkConfirmation.
 */
export interface UseBulkConfirmationParams {
  /** Resource type for the bulk endpoint (e.g. 'asset', 'seed', 'technology', 'risk') */
  resource: 'asset' | 'seed' | 'technology' | 'risk';
  /** Action type for the bulk endpoint */
  action:
    | 'status_change'
    | 'severity_change'
    | 'tag'
    | 'untag'
    | 'remove_tag'
    | 'comment';
  /**
   * The bulk-selection return value from useBulkSelection.
   * toBulkPayload() is called when the user confirms the dialog.
   */
  bulkSelection: Pick<UseBulkSelectionReturn, 'toBulkPayload'>;
  /**
   * Called after a successful bulk submission to clear the row selection.
   *
   * IMPORTANT: This must NOT call queryClient.invalidateQueries().
   * Query invalidation is handled exclusively by the useActiveBulkJobs
   * polling hook on terminal job state. Calling invalidateQueries here
   * would race with the async Lambda job execution.
   */
  clearSelection: () => void;
}

/**
 * Return value from useBulkConfirmation.
 */
export interface UseBulkConfirmationResult {
  /** Current confirmation payload, or null if the dialog is closed. */
  confirmPayload: BulkConfirmPayload | null;
  /** Open the confirmation dialog with the given payload. */
  openConfirmation: (payload: BulkConfirmPayload) => void;
  /** Close the confirmation dialog without submitting. */
  closeConfirmation: () => void;
  /**
   * Submit the confirmed bulk action and close the dialog.
   * No-op if confirmPayload is null.
   *
   * Wires clearSelection as the onSuccess callback so that row
   * de-selection happens after the job is accepted by the server.
   * Does NOT call invalidateQueries — see clearSelection note above.
   */
  submitConfirmed: () => void;
  /** True while the bulk mutation POST is in-flight. */
  isPending: boolean;
}

/**
 * Shared hook that encapsulates the bulk-action confirmation flow:
 * open dialog → user confirms → POST to /bulk/{resource} → clear selection.
 *
 * Extracted from the ~40-line inline pattern repeated across asset/seeds/
 * technology/vulnerabilities index.tsx files. Consuming components replace
 * their per-section useState + useBulkAction + BulkConfirmationDialog wiring
 * with a single useBulkConfirmation call.
 *
 * Polling responsibility: This hook does NOT invalidate queries. The
 * useActiveBulkJobs hook observes job state and invalidates the relevant
 * query cache when the job reaches a terminal state (completed/failed).
 *
 * @example
 * ```tsx
 * const statusConfirm = useBulkConfirmation({
 *   resource: 'asset',
 *   action: 'status_change',
 *   bulkSelection,
 *   clearSelection,
 * });
 *
 * // Open dialog from an action handler:
 * statusConfirm.openConfirmation({
 *   action: 'status_change',
 *   actionName: 'freeze',
 *   params: { status: 'F' },
 * });
 *
 * // In JSX:
 * <BulkConfirmationDialog
 *   isOpen={!!statusConfirm.confirmPayload}
 *   actionName={statusConfirm.confirmPayload?.actionName ?? ''}
 *   isPending={statusConfirm.isPending}
 *   onCancel={statusConfirm.closeConfirmation}
 *   onConfirm={statusConfirm.submitConfirmed}
 * />
 * ```
 */
export function useBulkConfirmation({
  resource,
  action,
  bulkSelection,
  clearSelection,
}: UseBulkConfirmationParams): UseBulkConfirmationResult {
  const [confirmPayload, setConfirmPayload] =
    useState<BulkConfirmPayload | null>(null);

  const { mutate: submitBulk, isPending } = useBulkAction({ resource, action });

  const submitConfirmed = () => {
    if (!confirmPayload) return;

    // Capture payload before clearing state so the closure is stable.
    const captured = confirmPayload;

    // Close dialog immediately — prevents double-submit if user clicks twice.
    setConfirmPayload(null);

    submitBulk(
      {
        action: captured.action,
        params: captured.params,
        selection: bulkSelection.toBulkPayload(),
      },
      {
        // Only clear the row selection UI state — do NOT invalidate queries.
        // Query invalidation is the responsibility of useActiveBulkJobs
        // (polls job status and invalidates on terminal state).
        onSuccess: clearSelection,
      }
    );
  };

  return {
    confirmPayload,
    openConfirmation: setConfirmPayload,
    closeConfirmation: () => setConfirmPayload(null),
    submitConfirmed,
    isPending,
  };
}
